import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Link, X, FileCheck, ChevronLeft, ChevronRight, Plus, Trash2, Edit2, Save } from 'lucide-react';
import PortalBackground from '../components/PortalBackground';

const ProposalsTemplate = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('proposal');
  const [currentPage, setCurrentPage] = useState(1);
  const [templateLink, setTemplateLink] = useState('');
  const [pagePDFs, setPagePDFs] = useState({
    proposal: {},
    contracts: {}
  });
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [customTitles, setCustomTitles] = useState({
    proposal: {},
    contracts: {}
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPagePDFs(prev => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          [currentPage]: file
        }
      }));
    } else {
      alert('Please upload a PDF file');
    }
  };

  const handleDeleteFile = () => {
    setPagePDFs(prev => {
      const newPDFs = { ...prev };
      delete newPDFs[activeTab][currentPage];
      return newPDFs;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', { templateLink, pagePDFs });
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleAddPage = () => {
    const totalPages = getTotalPages();
    const newPageNumber = totalPages + 1;
    
    // Add new page title
    setCustomTitles(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [newPageNumber]: `New Page ${newPageNumber}`
      }
    }));

    // Initialize empty PDF slot for new page
    setPagePDFs(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [newPageNumber]: null
      }
    }));
  };

  const handleDeletePage = () => {
    const totalPages = getTotalPages();
    if (totalPages > 1) {
      // Delete current page's PDF
      setPagePDFs(prev => {
        const newPDFs = { ...prev };
        delete newPDFs[activeTab][currentPage];
        // Shift remaining PDFs up
        for (let i = currentPage + 1; i <= totalPages; i++) {
          if (newPDFs[activeTab][i]) {
            newPDFs[activeTab][i - 1] = newPDFs[activeTab][i];
            delete newPDFs[activeTab][i];
          }
        }
        return newPDFs;
      });

      // Delete current page's title
      setCustomTitles(prev => {
        const newTitles = { ...prev };
        delete newTitles[activeTab][currentPage];
        // Shift remaining titles up
        for (let i = currentPage + 1; i <= totalPages; i++) {
          if (newTitles[activeTab][i]) {
            newTitles[activeTab][i - 1] = newTitles[activeTab][i];
            delete newTitles[activeTab][i];
          }
        }
        return newTitles;
      });

      // Update current page if needed
      if (currentPage === totalPages) {
        setCurrentPage(totalPages - 1);
      }
    }
  };

  const handleEditTitle = () => {
    setIsEditingTitle(true);
    setEditedTitle(getPageTitle(currentPage));
  };

  const handleSaveTitle = () => {
    setCustomTitles(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [currentPage]: editedTitle
      }
    }));
    setIsEditingTitle(false);
  };

  const getTotalPages = () => {
    const defaultPages = 6;
    const customPages = Object.keys(customTitles[activeTab]).length;
    return Math.max(defaultPages, customPages);
  };

  const getPageTitle = (page) => {
    // First check for custom title
    if (customTitles[activeTab][page]) {
      return customTitles[activeTab][page];
    }

    // Then use default titles
    if (activeTab === 'proposal') {
      const proposalTitles = {
        1: "Cover Page",
        2: "Scope of Work",
        3: "Proposed Budget",
        4: "Timeline",
        5: "Next Steps",
        6: "About Us"
      };
      return proposalTitles[page] || `Page ${page}`;
    } else {
      const contractTitles = {
        1: "Overview",
        2: "Agreement Page 1",
        3: "Agreement Page 2",
        4: "Agreement Page 3",
        5: "Agreement Page 4",
        6: "Signatures"
      };
      return contractTitles[page] || `Page ${page}`;
    }
  };

  const getCurrentPagePDF = () => {
    return pagePDFs[activeTab][currentPage] || null;
  };

  return (
    <div className="relative min-h-screen p-6">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        {/* Title Row with Back Button */}
        <div className="flex items-center mb-8 relative">
          <button
            onClick={() => navigate('/crm/templates')}
            className="flex items-center text-gray-600 hover:text-gray-900 absolute left-0"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Back to Templates
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white w-full text-center">Proposals Template</h1>
        </div>

        {/* Tabs */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex justify-center space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => {
                  setActiveTab('proposal');
                  setCurrentPage(1);
                }}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'proposal'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <FileText className="w-5 h-5 mr-2" />
                Proposal
              </button>
              <button
                onClick={() => {
                  setActiveTab('contracts');
                  setCurrentPage(1);
                }}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'contracts'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Contract
              </button>
            </nav>
          </div>
        </div>

        {/* Page Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>
            <button
              onClick={handleDeletePage}
              disabled={getTotalPages() <= 1}
              className={`flex items-center px-4 py-2 rounded-md ${
                getTotalPages() <= 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <Trash2 className="w-5 h-5 mr-2" />
              Delete Page
            </button>
          </div>
          <span className="text-sm font-medium text-gray-600">
            Page {currentPage} of {getTotalPages()}
          </span>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleAddPage}
              className="flex items-center px-4 py-2 rounded-md text-red-600 hover:bg-red-50"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Page
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === getTotalPages()}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === getTotalPages()
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-red-600 hover:bg-red-50'
              }`}
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6">
          <div className="flex items-center justify-center mb-4">
            {isEditingTitle ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-lg font-semibold text-gray-900 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                  autoFocus
                />
                <button
                  onClick={handleSaveTitle}
                  className="text-green-600 hover:text-green-700"
                >
                  <Save className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsEditingTitle(false)}
                  className="text-gray-600 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-gray-900">{getPageTitle(currentPage)}</h2>
                <button
                  onClick={handleEditTitle}
                  className="text-gray-600 hover:text-gray-700"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <div className="space-y-6">
            {/* Template Link Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                    <Link className="h-5 w-5" />
                  </span>
                  <input
                    type="url"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    value={templateLink}
                    onChange={(e) => setTemplateLink(e.target.value)}
                    placeholder="Enter template link"
                    required
                  />
                </div>
              </div>
            </div>

            {/* PDF Upload Section */}
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <div className="flex justify-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                </div>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="page-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-red-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="page-upload"
                      name="page-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                      accept=".pdf"
                      required
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF up to 10MB
                </p>
              </div>
            </div>

            {/* File Preview */}
            {getCurrentPagePDF() && (
              <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{getCurrentPagePDF().name}</span>
                </div>
                <button
                  type="button"
                  onClick={handleDeleteFile}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center mt-6">
          <button
            type="submit"
            className="px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProposalsTemplate; 